// Technical indicator calculations operating on candle arrays.
// Each candle: {time, open, high, low, close, volume}
// Overlay outputs: [{time, value}]  (nulls filtered)

const closes = (c) => c.map((x) => x.close);

export function sma(candles, period = 20) {
  const cs = closes(candles);
  const out = [];
  for (let i = 0; i < cs.length; i++) {
    if (i < period - 1) continue;
    let s = 0;
    for (let j = i - period + 1; j <= i; j++) s += cs[j];
    out.push({ time: candles[i].time, value: s / period });
  }
  return out;
}

export function ema(candles, period = 20) {
  const cs = closes(candles);
  const out = [];
  const k = 2 / (period + 1);
  let prev;
  for (let i = 0; i < cs.length; i++) {
    if (i === period - 1) {
      let s = 0;
      for (let j = 0; j < period; j++) s += cs[j];
      prev = s / period;
      out.push({ time: candles[i].time, value: prev });
    } else if (i >= period) {
      prev = cs[i] * k + prev * (1 - k);
      out.push({ time: candles[i].time, value: prev });
    }
  }
  return out;
}

function emaArray(arr, period) {
  const out = new Array(arr.length).fill(null);
  const k = 2 / (period + 1);
  let prev;
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] == null) continue;
    if (prev == null) prev = arr[i];
    else prev = arr[i] * k + prev * (1 - k);
    out[i] = prev;
  }
  return out;
}

export function bollinger(candles, period = 20, mult = 2) {
  const cs = closes(candles);
  const upper = [], mid = [], lower = [];
  for (let i = period - 1; i < cs.length; i++) {
    const slice = cs.slice(i - period + 1, i + 1);
    const m = slice.reduce((a, b) => a + b, 0) / period;
    const sd = Math.sqrt(slice.reduce((a, b) => a + (b - m) ** 2, 0) / period);
    upper.push({ time: candles[i].time, value: m + mult * sd });
    mid.push({ time: candles[i].time, value: m });
    lower.push({ time: candles[i].time, value: m - mult * sd });
  }
  return { upper, mid, lower };
}

export function vwap(candles) {
  let cumPV = 0, cumV = 0;
  return candles.map((c) => {
    const tp = (c.high + c.low + c.close) / 3;
    cumPV += tp * c.volume;
    cumV += c.volume;
    return { time: c.time, value: cumV ? cumPV / cumV : c.close };
  });
}

export function rsi(candles, period = 14) {
  const cs = closes(candles);
  const out = [];
  let gain = 0, loss = 0;
  for (let i = 1; i < cs.length; i++) {
    const ch = cs[i] - cs[i - 1];
    if (i <= period) {
      if (ch >= 0) gain += ch; else loss -= ch;
      if (i === period) {
        gain /= period; loss /= period;
        const rs = loss === 0 ? 100 : gain / loss;
        out.push({ time: candles[i].time, value: 100 - 100 / (1 + rs) });
      }
    } else {
      const g = ch >= 0 ? ch : 0;
      const l = ch < 0 ? -ch : 0;
      gain = (gain * (period - 1) + g) / period;
      loss = (loss * (period - 1) + l) / period;
      const rs = loss === 0 ? 100 : gain / loss;
      out.push({ time: candles[i].time, value: 100 - 100 / (1 + rs) });
    }
  }
  return out;
}

export function macd(candles, fast = 12, slow = 26, signal = 9) {
  const cs = closes(candles);
  const ef = emaArray(cs, fast);
  const es = emaArray(cs, slow);
  const macdLine = cs.map((_, i) => (ef[i] != null && es[i] != null ? ef[i] - es[i] : null));
  const sig = emaArray(macdLine, signal);
  const line = [], signalLine = [], hist = [];
  for (let i = 0; i < cs.length; i++) {
    if (macdLine[i] != null) line.push({ time: candles[i].time, value: macdLine[i] });
    if (sig[i] != null) signalLine.push({ time: candles[i].time, value: sig[i] });
    if (macdLine[i] != null && sig[i] != null) hist.push({ time: candles[i].time, value: macdLine[i] - sig[i] });
  }
  return { line, signalLine, hist };
}

export function stochastic(candles, period = 14, k = 3) {
  const out = [];
  for (let i = period - 1; i < candles.length; i++) {
    const slice = candles.slice(i - period + 1, i + 1);
    const hi = Math.max(...slice.map((c) => c.high));
    const lo = Math.min(...slice.map((c) => c.low));
    const val = hi === lo ? 50 : ((candles[i].close - lo) / (hi - lo)) * 100;
    out.push({ time: candles[i].time, value: val });
  }
  return out;
}

export function cci(candles, period = 20) {
  const out = [];
  for (let i = period - 1; i < candles.length; i++) {
    const slice = candles.slice(i - period + 1, i + 1);
    const tps = slice.map((c) => (c.high + c.low + c.close) / 3);
    const m = tps.reduce((a, b) => a + b, 0) / period;
    const md = tps.reduce((a, b) => a + Math.abs(b - m), 0) / period;
    const tp = (candles[i].high + candles[i].low + candles[i].close) / 3;
    out.push({ time: candles[i].time, value: md === 0 ? 0 : (tp - m) / (0.015 * md) });
  }
  return out;
}

export function atr(candles, period = 14) {
  const trs = [];
  for (let i = 1; i < candles.length; i++) {
    const c = candles[i], p = candles[i - 1];
    trs.push(Math.max(c.high - c.low, Math.abs(c.high - p.close), Math.abs(c.low - p.close)));
  }
  const out = [];
  let prev;
  for (let i = 0; i < trs.length; i++) {
    if (i === period - 1) {
      prev = trs.slice(0, period).reduce((a, b) => a + b, 0) / period;
      out.push({ time: candles[i + 1].time, value: prev });
    } else if (i >= period) {
      prev = (prev * (period - 1) + trs[i]) / period;
      out.push({ time: candles[i + 1].time, value: prev });
    }
  }
  return out;
}

export function adx(candles, period = 14) {
  // simplified ADX
  const out = [];
  let prevTr = 0, prevPlus = 0, prevMinus = 0, prevAdx;
  const dxs = [];
  for (let i = 1; i < candles.length; i++) {
    const c = candles[i], p = candles[i - 1];
    const up = c.high - p.high;
    const down = p.low - c.low;
    const plusDM = up > down && up > 0 ? up : 0;
    const minusDM = down > up && down > 0 ? down : 0;
    const tr = Math.max(c.high - c.low, Math.abs(c.high - p.close), Math.abs(c.low - p.close));
    prevTr = prevTr - prevTr / period + tr;
    prevPlus = prevPlus - prevPlus / period + plusDM;
    prevMinus = prevMinus - prevMinus / period + minusDM;
    const pdi = prevTr ? (prevPlus / prevTr) * 100 : 0;
    const mdi = prevTr ? (prevMinus / prevTr) * 100 : 0;
    const dx = pdi + mdi ? (Math.abs(pdi - mdi) / (pdi + mdi)) * 100 : 0;
    dxs.push(dx);
    if (dxs.length >= period) {
      if (prevAdx == null) prevAdx = dxs.slice(-period).reduce((a, b) => a + b, 0) / period;
      else prevAdx = (prevAdx * (period - 1) + dx) / period;
      out.push({ time: c.time, value: prevAdx });
    }
  }
  return out;
}

export function parabolicSar(candles, step = 0.02, max = 0.2) {
  const out = [];
  if (candles.length < 2) return out;
  let up = candles[1].close > candles[0].close;
  let sar = up ? candles[0].low : candles[0].high;
  let ep = up ? candles[0].high : candles[0].low;
  let af = step;
  for (let i = 1; i < candles.length; i++) {
    const c = candles[i];
    sar = sar + af * (ep - sar);
    if (up) {
      if (c.low < sar) { up = false; sar = ep; ep = c.low; af = step; }
      else { if (c.high > ep) { ep = c.high; af = Math.min(af + step, max); } }
    } else {
      if (c.high > sar) { up = true; sar = ep; ep = c.high; af = step; }
      else { if (c.low < ep) { ep = c.low; af = Math.min(af + step, max); } }
    }
    out.push({ time: c.time, value: sar });
  }
  return out;
}

export function ichimoku(candles) {
  const conv = [], base = [], spanA = [], spanB = [];
  const hh = (arr) => Math.max(...arr.map((c) => c.high));
  const ll = (arr) => Math.min(...arr.map((c) => c.low));
  for (let i = 0; i < candles.length; i++) {
    if (i >= 8) {
      const s = candles.slice(i - 8, i + 1);
      conv.push({ time: candles[i].time, value: (hh(s) + ll(s)) / 2 });
    }
    if (i >= 25) {
      const s = candles.slice(i - 25, i + 1);
      base.push({ time: candles[i].time, value: (hh(s) + ll(s)) / 2 });
    }
  }
  return { conv, base, spanA, spanB };
}

export const INDICATOR_META = {
  MA: { label: "Moving Average (SMA)", type: "overlay" },
  EMA: { label: "EMA", type: "overlay" },
  BB: { label: "Bollinger Bands", type: "overlay" },
  VWAP: { label: "VWAP", type: "overlay" },
  SAR: { label: "Parabolic SAR", type: "overlay" },
  ICHIMOKU: { label: "Ichimoku Cloud", type: "overlay" },
  RSI: { label: "RSI", type: "oscillator" },
  MACD: { label: "MACD", type: "oscillator" },
  STOCH: { label: "Stochastic", type: "oscillator" },
  CCI: { label: "CCI", type: "oscillator" },
  ADX: { label: "ADX", type: "oscillator" },
  ATR: { label: "ATR", type: "oscillator" },
  VOL: { label: "Volume", type: "oscillator" },
};
